import React from 'react';
import type { MatchSession } from 'match';

type MatchScheduleProps = {
    sessions: MatchSession[];
    onBack: () => void;
    onHelp?: () => void;
    onBookSession: (sessionId: string) => void;
};

const weekDayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const calendarCells = [
    { label: '29', muted: true },
    { label: '30', muted: true },
    { label: '31', muted: true },
    { label: '1' },
    { label: '2' },
    { label: '3' },
    { label: '4' },
    { label: '5' },
    { label: '6' },
    { label: '7' },
    { label: '8' },
    { label: '9' },
    { label: '10' },
    { label: '11' },
    { label: '12' },
    { label: '13' },
    { label: '14' },
    { label: '15', active: true },
    { label: '16', dot: true },
    { label: '17', dot: true },
    { label: '18' },
    { label: '19' },
    { label: '20' },
    { label: '21' },
    { label: '22' },
    { label: '23' },
    { label: '24' },
    { label: '25' },
    { label: '26' },
    { label: '27' },
    { label: '28' },
    { label: '29', muted: true },
    { label: '30', muted: true }
];

export function MatchSchedule({ sessions, onBack, onHelp, onBookSession }: MatchScheduleProps) {
    return (
        <div className="min-h-screen bg-slate-100 px-4 py-12">
            <div className="mx-auto w-full max-w-sm overflow-hidden rounded-[32px] bg-white shadow-2xl shadow-slate-900/10">
                <header className="flex items-center justify-between px-5 pt-5">
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-lg text-slate-500"
                        aria-label="Go back"
                    >
                        ←
                    </button>
                    <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold uppercase text-white">
                            GC
                        </span>
                        <span className="text-base font-semibold text-slate-900">Schedule</span>
                    </div>
                    <button
                        type="button"
                        onClick={onHelp}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-500"
                        aria-label="Help"
                    >
                        ?
                    </button>
                </header>

                <main className="px-6 pb-12">
                    <section className="mt-6 rounded-[28px] border border-slate-200/80 bg-white shadow-[0_10px_25px_-20px_rgba(15,23,42,0.35)]">
                        <header className="flex items-center justify-between px-6 pt-6">
                            <div>
                                <p className="text-[18px] font-semibold text-slate-900">September 2025</p>
                                <p className="text-sm text-slate-500">Wed 15</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-sm text-slate-500"
                                    aria-label="Previous month"
                                >
                                    ◀
                                </button>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-sm text-slate-500"
                                    aria-label="Next month"
                                >
                                    ▶
                                </button>
                            </div>
                        </header>

                        <div className="mt-5 border-t border-slate-200/80 px-6 pb-6">
                            <div className="grid grid-cols-7 gap-y-3 pt-4 text-center text-[13px] font-medium text-slate-400">
                                {weekDayLabels.map(label => (
                                    <span key={label}>{label}</span>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-y-3 pt-2 text-center text-sm">
                                {calendarCells.map(cell => (
                                    <div key={`${cell.label}-${cell.muted ? 'muted' : 'active'}`} className="flex w-full flex-col items-center gap-1">
                                        <span
                                            className={
                                                cell.active
                                                    ? 'flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-base font-semibold text-white'
                                                    : `flex h-9 w-9 items-center justify-center rounded-full text-base font-semibold ${cell.muted ? 'text-slate-300' : 'text-slate-600'}`
                                            }
                                        >
                                            {cell.label}
                                        </span>
                                        {cell.dot ? <span className="h-2 w-2 rounded-full bg-emerald-400" /> : <span className="h-2 w-2" />}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="mt-8 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-900">Availability</h3>
                            <p className="text-sm text-slate-500">Jan 15, 2025</p>
                        </div>

                        {sessions.map(session => (
                            <article key={session.id} className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_12px_25px_-18px_rgba(15,23,42,0.22)]">
                                <header className="space-y-1 text-left">
                                    <h4 className="text-sm font-semibold text-slate-900">{session.title}</h4>
                                    <p className="text-sm text-slate-500">With {session.guideName}</p>
                                    <p className="flex items-center gap-3 text-sm font-medium text-slate-700">
                                        <span>🕑 {session.timeRange}</span>
                                        <span>💻 {session.mode}</span>
                                    </p>
                                </header>
                                <button
                                    type="button"
                                    onClick={() => onBookSession(session.id)}
                                    className="mt-5 inline-flex w-full items-center justify-center rounded-[14px] bg-slate-900 py-3 text-sm font-semibold text-white"
                                >
                                    Book Session
                                </button>
                            </article>
                        ))}
                    </section>

                    <section className="mt-10 rounded-[22px] border border-slate-100 bg-slate-50/90 p-5">
                        <h3 className="text-base font-semibold text-slate-900">What to expect in your session</h3>
                        <ul className="mt-4 space-y-3 text-sm text-slate-600">
                            <li className="flex items-start gap-3">
                                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                                    ★
                                </span>
                                Get to know your Guide
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                                    ★
                                </span>
                                Talk about your specific needs
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                                    ★
                                </span>
                                Experienced in your religion
                            </li>
                        </ul>
                    </section>
                </main>
            </div>
        </div>
    );
}
